import {
  coerceMultipleChoiceState,
  type MagnusUIMessage,
  MULTIPLE_CHOICE_TOOL_NAME,
  type SdkMultipleChoiceState,
} from "@bg-bgi/magnus-sdk";
import { isToolPart, toolPartName } from "../../tool-part";

/**
 * A single unanswered `multipleChoice` tool call.
 */
export type ActiveMultipleChoicePrompt = {
  toolCallId: string;
  prompt: SdkMultipleChoiceState;
};

/**
 * Every unanswered `multipleChoice` tool call from the latest assistant
 * turn, which the chat input surfaces as a dedicated picker mode
 * (replacing the standard composer) until the user submits or skips.
 */
export type ActiveMultipleChoice = {
  /** Prompts in the order the model called them. */
  prompts: ActiveMultipleChoicePrompt[];
  /** Joined tool call ids — a stable mount key for the picker. */
  key: string;
  /**
   * When false, the picker is shown (e.g. while the assistant turn is still
   * streaming) but Submit and Skip must stay disabled until the stream ends.
   */
  canSubmit: boolean;
};

/**
 * Returns the `multipleChoice` prompts the user still needs to answer, or
 * `null` if there are none.
 *
 * Prompts are "active" when the latest message is an assistant turn that
 * asked at least one `multipleChoice` question. The picker can appear while
 * `status === "streaming"` as soon as a tool part has a usable prompt;
 * `canSubmit` is false until `status === "ready"` so an answer cannot
 * race the in-flight turn. Both answering AND skipping send a new user
 * message, so the tool calls stop being the last message and the picker
 * reverts to the standard input automatically — no per-session "skipped"
 * tracking required.
 *
 * The model sometimes calls `multipleChoice` twice in the same step (the
 * workflow's `hasToolCall` stop condition only ends the turn once the step
 * completes, so both calls land in one assistant message). All of them are
 * collected, in call order, so the picker can ask every question in one
 * batch and send a single combined answer — answering one at a time is not
 * an option, since each answer is a user message that would start a fresh
 * turn and orphan the remaining prompts. Trailing assistant messages are
 * scanned too, so a text-only follow-up message can't hide a prompt.
 *
 * Each prompt is read from its tool part's `output` when present, but falls
 * back to its `input`: the two carry the same shape, and on a reload from
 * history the persisted part may no longer be `output-available` (the
 * turn stops on the call before the server-assembled message merges the
 * result, and the persist step's tool-call repair can rewrite it to
 * `output-error`). Keying off the input keeps the picker alive across
 * navigation since the question/options always live there. A part that
 * can't be coerced yet (mid-stream, options still arriving) is skipped
 * rather than hiding its siblings; it joins the batch once it lands, and
 * `canSubmit` blocks submitting until the whole turn is done.
 */
export function getActiveMultipleChoice(
  messages: readonly MagnusUIMessage[],
  status: "ready" | "submitted" | "streaming" | "error"
): ActiveMultipleChoice | null {
  if (status === "error") {
    return null;
  }

  const last = messages.at(-1);
  if (!last || last.role !== "assistant") {
    return null;
  }

  const prompts: ActiveMultipleChoicePrompt[] = [];
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (!message || message.role !== "assistant") {
      break;
    }
    // Prepend so the overall list stays in conversation order while the
    // outer loop walks backwards.
    prompts.unshift(...collectPrompts(message));
  }

  if (prompts.length === 0) {
    return null;
  }

  return {
    prompts,
    key: prompts.map((p) => p.toolCallId).join("|"),
    canSubmit: status === "ready",
  };
}

function collectPrompts(
  message: MagnusUIMessage
): ActiveMultipleChoicePrompt[] {
  const found: ActiveMultipleChoicePrompt[] = [];
  for (const raw of message.parts) {
    const part = raw as { type: string } | undefined;
    if (
      !(part && isToolPart(part)) ||
      toolPartName(part) !== MULTIPLE_CHOICE_TOOL_NAME
    ) {
      continue;
    }
    if (!part.toolCallId) {
      continue;
    }
    const prompt =
      coerceMultipleChoiceState(part.output) ??
      coerceMultipleChoiceState(part.input);
    if (!prompt) {
      continue;
    }
    found.push({ toolCallId: part.toolCallId, prompt });
  }
  return found;
}
